import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { Dispatch, Ref, SetStateAction } from 'react';

import { isWeb, useComposedRefs } from '@tamagui/core';
import type { TextInput as RNTextInput } from 'react-native';

import type { InputProps, InputStateProp } from './InputProps';

export const useInputId = (id: InputProps['id']) => {
  const backupId = useId();
  return id || backupId;
};

/**
 * There's a bug in tamagui where the `name` attribute doesn't get set on
 * styled components. See: https://github.com/tamagui/tamagui/issues/2495
 *
 * Use this hook to manually set the `name` attribute on styled components. Note
 * that this attribute must be used on inputs if you want to use the FormData
 * API (without it, `new FormData(<form element>)` will be empty).
 */
export const useInputName = (name: InputProps['name']) => {
  // Have to use `any` because we need it to be an HTMLElement here but
  // elsewhere we use React Native types, which don't have `setAttribute`
  // (in reality, `setAttribute` will exist when `isWeb` is true)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ref = useRef<any>(null);
  useEffect(() => {
    if (isWeb && name && ref.current instanceof HTMLElement) {
      ref.current?.setAttribute('name', name);
    }
  }, [name]);
  return ref;
};

export const useFrameFocus = (ref: Ref<RNTextInput>) => {
  const focusRef = useRef<RNTextInput>(null);
  const inputRef = useComposedRefs(ref, focusRef);

  const handleFramePress = () => {
    focusRef.current?.focus();
  };

  return { inputRef, handleFramePress };
};

export type InputStateHookProps<EventType> = Pick<
  InputProps,
  'disabled' | 'errorText' | 'invalid'
> & {
  onFocus?: (event: EventType) => void;
  onBlur?: (event: EventType) => void;
};
export type InputHookOutput<EventType> = {
  handleFocus: NonNullable<(event: EventType) => void>;
  handleBlur: NonNullable<(event: EventType) => void>;
  setFocused: Dispatch<SetStateAction<boolean>>;
};

export const useInputState = <EventType>({
  disabled,
  errorText,
  invalid,
  onFocus,
  onBlur,
}: InputStateHookProps<EventType>): InputHookOutput<EventType> & InputStateProp => {
  const [focused, setFocused] = useState(false);

  const handleFocus = useCallback(
    (e: EventType) => {
      setFocused(true);
      onFocus && onFocus(e);
    },
    [onFocus],
  );

  const handleBlur = useCallback(
    (e: EventType) => {
      setFocused(false);
      onBlur && onBlur(e);
    },
    [onBlur],
  );

  const state = disabled
    ? 'inactive'
    : errorText || invalid
      ? 'error'
      : focused
        ? 'focused'
        : undefined;

  return { state, handleFocus, handleBlur, setFocused };
};
